import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  CheckCircle,
  HourglassEmpty,
  Cancel,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { ClipboardList } from 'mdi-material-ui';
import { ReactComponent as Logo } from 'images/logo.svg';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PlanSplitCard from 'components/plan/PlanSplitCard';
import couponApi from 'api/rest/coupon';
import AppBarContext from 'context/AppBarContext';
import uiSlice from 'store/modules/ui';
import { createSnack } from 'common/utils/general';
import routes from 'common/routes';
import useIpGeolocation from 'hooks/payments/useIpGeolocation';
import paymentApi from 'api/rest/payment';

// Curva de salida premium (soft-skill), igual que los counters del dashboard.
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Card monocroma estilo "counter del inicio" (Último pago / Agenda): superficie off-black
// elevada con hairline, sombra teñida e inner-highlight; en hover se levanta hacia #282828.
const planCardSx = {
  position: 'relative',
  height: '100%',
  overflow: 'hidden',
  p: { xs: 2, md: 2.5 },
  borderRadius: '16px',
  backgroundColor: 'surface.raised',
  border: '1px solid',
  borderColor: 'hairline.main',
  boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
  transition: `transform 0.25s ${EASE}, box-shadow 0.25s ${EASE}, border-color 0.25s ${EASE}, background-color 0.25s ${EASE}`,
  '&:hover': {
    transform: 'translateY(-3px)',
    backgroundColor: 'surface.hover',
    borderColor: 'hairline.strong',
    boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow-hover)'
  }
};

// Watermark del logo OTRIX en la esquina (igual que los counters del dashboard).
const watermarkLogoStyles = {
  height: '100%',
  opacity: 0.04,
  position: 'absolute',
  right: 0,
  top: 0,
  transform: 'translateX(45%)',
  pointerEvents: 'none'
};

const MY_PLAN_REQUESTS_QUERY = gql`
  query MyPlanRequests {
    myPlanRequests {
      id
      status
      rejection_reason
      reviewed_at
      created_at
      plan {
        id
        name
        sessions
        price_cop
        price_usd
      }
    }
  }
`;

const MY_RECENT_PLANS_QUERY = gql`
  query MyRecentPlans {
    myRecentPlans {
      last_paid_at
      plan {
        id
        name
        sessions
        price_cop
        price_usd
      }
    }
  }
`;

const ALL_PLANS_QUERY = gql`
  query AllPlansForRequest {
    allPlans {
      id
      name
      sessions
      price_cop
      price_usd
      is_subscribable
      platform
      image_url
      description
      active_discount_percent
      org_benefit {
        discount_percent
        base_price
        discounted_price
        currency
        organization_name
      }
    }
  }
`;

// Active Plus SIEMPRE primero (igual que en Inicio).
const PLATFORM_ORDER = ['active_plus', 'otrix_app', 'otrix_play', 'otrix_gym'];
const PLATFORM_LABELS = {
  active_plus: 'Otrix Active Plus',
  otrix_app: 'Otrix Personalizado Online',
  otrix_play: 'OTRIX Play',
  otrix_gym: 'OTRIX Gym'
};

const REQUEST_PLAN_ACQUISITION_MUTATION = gql`
  mutation RequestPlanAcquisition($plan_id: ID!, $coupon_code: String) {
    requestPlanAcquisition(plan_id: $plan_id, coupon_code: $coupon_code) {
      id
      status
    }
  }
`;

function formatPrice(plan) {
  if (!plan) return '';
  if (plan.price_usd) return `$${plan.price_usd} USD`;
  if (plan.price_cop) return `$${Number(plan.price_cop).toLocaleString('es-CO')} COP`;
  return '';
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

const planShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  sessions: PropTypes.number,
  price_cop: PropTypes.number,
  price_usd: PropTypes.number,
  active_discount_percent: PropTypes.number
});

function RecentPlanCard({ item, onRenew }) {
  return (
    <Box sx={planCardSx}>
      <Logo style={watermarkLogoStyles} />
      <Stack spacing={1.5} sx={{ position: 'relative' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="subtitle1" fontWeight={700}>
            {item.plan.name}
          </Typography>
          <Chip
            label="Anterior"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, color: 'text.secondary', borderColor: 'hairline.strong' }}
          />
        </Stack>

        <Typography variant="caption" color="text.secondary">
          Último pago: {formatDate(item.last_paid_at)}
        </Typography>

        <Stack direction="row" spacing={3}>
          <Box>
            <Typography variant="caption" color="text.secondary">Sesiones</Typography>
            <Typography variant="body2" fontWeight={600}>{item.plan.sessions}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Precio actual</Typography>
            <Typography variant="body2" fontWeight={600}>{formatPrice(item.plan)}</Typography>
          </Box>
        </Stack>

        <Divider sx={{ borderColor: 'hairline.main' }} />

        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => onRenew(item.plan.id)}>
          Renovar
        </Button>
      </Stack>
    </Box>
  );
}

RecentPlanCard.propTypes = {
  item: PropTypes.shape({
    plan: planShape.isRequired,
    last_paid_at: PropTypes.string.isRequired
  }).isRequired,
  onRenew: PropTypes.func.isRequired
};

const fmtMoney = (v, c) => (c === 'USD' ? `$${Number(v).toFixed(2)} USD` : `$${Number(v).toLocaleString('es-CO')} COP`);

function AvailablePlanCard({
  plan,
  existingRequest = null,
  justRequested,
  onRequest,
  onSubscribe = () => {},
  canSubscribe = false,
  requesting
}) {
  const { t } = useTranslation(['common']);
  const hasPending = justRequested || existingRequest?.status === 'PENDING';
  const isApproved = existingRequest?.status === 'APPROVED';
  const isRejected = existingRequest?.status === 'REJECTED';
  const hasDiscount = (plan.active_discount_percent || 0) > 0;

  // Cupón (solo si el plan tiene descuento): ingresar código → validar → mostrar precio final.
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState(null);
  const [applying, setApplying] = useState(false);
  const [couponError, setCouponError] = useState('');

  const applyCoupon = async () => {
    setCouponError('');
    setApplying(true);
    try {
      const res = await couponApi.validateCoupon(code.trim(), plan.id);
      if (res?.valid) setApplied(res);
      else { setApplied(null); setCouponError(res?.reason || 'Cupón inválido o no aplica.'); }
    } catch {
      setCouponError('No se pudo validar el cupón.');
    } finally {
      setApplying(false);
    }
  };

  let badge = null;
  if (hasPending) {
    badge = <Chip label="En revisión" size="small" color="warning" icon={<HourglassEmpty fontSize="small" />} sx={{ fontWeight: 600, flexShrink: 0 }} />;
  } else if (isApproved) {
    badge = <Chip label="Aprobado" size="small" color="success" icon={<CheckCircle fontSize="small" />} sx={{ fontWeight: 600, flexShrink: 0 }} />;
  } else if (isRejected) {
    badge = <Chip label="Rechazado" size="small" color="error" icon={<Cancel fontSize="small" />} sx={{ fontWeight: 600, flexShrink: 0 }} />;
  }

  const requestLabel = (isApproved || isRejected) ? 'Solicitar de nuevo' : 'Solicitar plan';

  const actions = (
    <Stack spacing={1.25}>
      {isRejected && existingRequest?.rejection_reason && (
        <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'surface.overlay' }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Motivo</Typography>
          <Typography variant="body2">{existingRequest.rejection_reason}</Typography>
        </Box>
      )}
      {hasPending && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Solicitud en revisión. Un asesor te contactará pronto.
        </Alert>
      )}
      {isApproved && (
        <Alert severity="success" sx={{ py: 0.5 }}>
          Aprobado. Espera el enlace de pago de tu asesor (o vuelve a solicitarlo si tu plan venció).
        </Alert>
      )}

      {/* Campo de cupón: solo si el plan tiene descuento y no hay solicitud en revisión. */}
      {!hasPending && hasDiscount && (
        <Box sx={{ p: 1.25, borderRadius: '12px', border: '1px dashed', borderColor: 'gold.main', backgroundColor: 'gold.soft' }}>
          <Typography variant="caption" sx={{ color: 'gold.main', fontWeight: 700, display: 'block', mb: 0.75 }}>
            ¡Este plan tiene descuento! Ingresa tu cupón
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField size="small" fullWidth placeholder="Código del cupón" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setApplied(null); }} />
            <Button size="small" variant="outlined" color="inherit" onClick={applyCoupon} disabled={applying || !code.trim()} sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0, borderColor: 'gold.main', color: 'gold.main' }}>
              {applying ? '…' : 'Aplicar'}
            </Button>
          </Stack>
          {applied && (
            <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 800, color: 'gold.main' }}>
              Precio con descuento: {fmtMoney(applied.discounted_price, applied.currency)}
            </Typography>
          )}
          {couponError && <Typography variant="caption" sx={{ mt: 0.5, color: 'error.main', display: 'block' }}>{couponError}</Typography>}
        </Box>
      )}

      {!hasPending && canSubscribe && (
        <>
          <Button fullWidth variant="contained" size="small" onClick={() => onSubscribe(plan.id)}>
            {t('plan.subscribe', { ns: 'common', defaultValue: 'Suscribirme' })}
          </Button>
          <Typography variant="caption" textAlign="center" color="text.secondary">
            {t('plan.cancelAnytime', { ns: 'common', defaultValue: 'Cancela cuando quieras' })}
          </Typography>
        </>
      )}
      {!hasPending && !canSubscribe && (
        <Button fullWidth variant="contained" size="small" startIcon={<SendIcon />} disabled={requesting} onClick={() => onRequest(plan.id, applied ? code.trim() : null)}>
          {requestLabel}
        </Button>
      )}
    </Stack>
  );

  return <PlanSplitCard plan={plan} badge={badge} actions={actions} />;
}

AvailablePlanCard.propTypes = {
  plan: planShape.isRequired,
  existingRequest: PropTypes.shape({
    status: PropTypes.string,
    rejection_reason: PropTypes.string
  }),
  justRequested: PropTypes.bool.isRequired,
  onRequest: PropTypes.func.isRequired,
  onSubscribe: PropTypes.func,
  canSubscribe: PropTypes.bool,
  requesting: PropTypes.bool.isRequired
};

function ClientPlanRequestStatus() {
  const { t } = useTranslation(['common']);
  const { setAppBarTitle, setAppBarButton } = useContext(AppBarContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { country: userCountry } = useSelector((state) => state.auth.user);
  const { currency: detectedCurrency, gateway: detectedGateway, gatewayEnabled } = useIpGeolocation();
  const stripeReady = detectedGateway === 'stripe' && gatewayEnabled;

  const userCurrency = useMemo(() => {
    if (detectedCurrency) return detectedCurrency;
    try {
      const label = JSON.parse(userCountry)?.label;
      return label === 'Colombia' ? 'COP' : 'USD';
    } catch {
      return 'USD';
    }
  }, [detectedCurrency, userCountry]);

  const isInternational = userCurrency === 'USD';

  const [requestedPlanIds, setRequestedPlanIds] = useState([]);
  const [openPlatform, setOpenPlatform] = useState(null);

  const { data, loading } = useQuery(MY_PLAN_REQUESTS_QUERY, {
    fetchPolicy: 'network-only'
  });

  const { data: recentData, loading: recentLoading } = useQuery(MY_RECENT_PLANS_QUERY, {
    fetchPolicy: 'network-only',
    skip: isInternational
  });

  const { data: allPlansData, loading: allPlansLoading } = useQuery(ALL_PLANS_QUERY, {
    fetchPolicy: 'network-only',
    skip: !isInternational
  });

  const [requestAcquisition, { loading: requesting }] = useMutation(
    REQUEST_PLAN_ACQUISITION_MUTATION
  );

  useEffect(() => {
    setAppBarTitle(t('plan.requests.myTitle', { ns: 'common' }));
    setAppBarButton(null);
  }, [setAppBarTitle, setAppBarButton, t]);

  const statusConfig = {
    PENDING:  { label: t('plan.requests.statusLabels.PENDING_REVIEW', { ns: 'common' }), color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
    APPROVED: { label: t('plan.requests.statusLabels.APPROVED', { ns: 'common' }),       color: 'success', icon: <CheckCircle fontSize="small" /> },
    REJECTED: { label: t('plan.requests.statusLabels.REJECTED', { ns: 'common' }),       color: 'error',   icon: <Cancel fontSize="small" /> }
  };

  const requests = data?.myPlanRequests ?? [];
  const recentPlans = recentData?.myRecentPlans ?? [];
  const allPlans = allPlansData?.allPlans ?? [];

  // Agrupa por plataforma (Active Plus primero) para mostrarlos como en Inicio.
  const plansByPlatform = useMemo(() => PLATFORM_ORDER.reduce((acc, p) => {
    const filtered = allPlans.filter((plan) => (plan.platform || 'otrix_app') === p);
    if (filtered.length > 0) acc[p] = filtered;
    return acc;
  }, {}), [allPlans]);

  // Abre por defecto el primer grupo disponible.
  useEffect(() => {
    if (openPlatform === null) {
      const first = Object.keys(plansByPlatform)[0];
      if (first) setOpenPlatform(first);
    }
  }, [plansByPlatform, openPlatform]);

  const requestsByPlanId = useMemo(() => {
    const map = {};
    requests.forEach((r) => { map[String(r.plan.id)] = r; });
    return map;
  }, [requests]);

  const handleRenew = (planId) => {
    navigate(`${routes.listClientPayments.client.path}?action=renew&plan_id=${planId}`);
  };

  const handleSubscribe = (planId) => {
    paymentApi
      .createStripeSubscriptionCheckout({ plan_id: planId })
      .then(({ url }) => { window.location.href = url; })
      .catch((err) => {
        dispatch(
          uiSlice.actions.enqueueSnackbar(
            createSnack({
              key: 'stripe-sub-error',
              variant: 'error',
              message: err?.message || t('plan.subscribeError', { ns: 'common', defaultValue: 'No pudimos iniciar la suscripción.' })
            })
          )
        );
      });
  };

  const handleRequest = async (planId, couponCode = null) => {
    try {
      await requestAcquisition({ variables: { plan_id: planId, coupon_code: couponCode || null } });
      setRequestedPlanIds((prev) => [...prev, String(planId)]);
    } catch {
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({
            key: 'plan-request-error',
            variant: 'error',
            message: 'Hubo un error al enviar tu solicitud. Intenta de nuevo.'
          })
        )
      );
    }
  };

  // Vista internacional (USD)
  if (isInternational) {
    return (
      <>
        <SEOPageTitle title={t('plan.requests.myTitle', { ns: 'common' })} />

        <Stack spacing={3}>
          <Alert severity="info" variant="filled">
            Solicita el plan que desees y un asesor de OTRIX te enviará el enlace de pago.
          </Alert>

          {(loading || allPlansLoading) && (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={28} color="secondary" />
            </Stack>
          )}

          {!loading && !allPlansLoading && allPlans.length > 0 && (
            <Stack spacing={2.5}>
              {Object.entries(plansByPlatform).map(([p, plans]) => {
                const isOpen = openPlatform === p;
                return (
                  <Box key={p}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={() => setOpenPlatform((prev) => (prev === p ? null : p))}
                      sx={{ px: 0.5, py: 1, mb: 1.5, cursor: 'pointer', borderBottom: '1px solid', borderColor: 'hairline.main' }}>
                      <Stack direction="row" spacing={1.25} alignItems="baseline">
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
                          {PLATFORM_LABELS[p]}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                          {plans.length} {plans.length === 1 ? 'plan' : 'planes'}
                        </Typography>
                      </Stack>
                      <IconButton size="small" aria-label={isOpen ? 'contraer' : 'expandir'} sx={{ color: isOpen ? 'gold.main' : 'text.secondary', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1)' }}>
                        <ExpandMoreIcon />
                      </IconButton>
                    </Stack>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Grid container spacing={{ xs: 2, lg: 3 }} alignItems="stretch" sx={{ pb: { xs: 1, lg: 2 } }}>
                        {plans.map((plan) => (
                          <Grid key={plan.id} item xs={12} md={6}>
                            <AvailablePlanCard
                              plan={plan}
                              existingRequest={requestsByPlanId[String(plan.id)] ?? null}
                              justRequested={requestedPlanIds.includes(String(plan.id))}
                              onRequest={handleRequest}
                              onSubscribe={handleSubscribe}
                              canSubscribe={stripeReady && plan.is_subscribable}
                              requesting={requesting}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  </Box>
                );
              })}
            </Stack>
          )}

          {!loading && !allPlansLoading && allPlans.length === 0 && (
            <Stack spacing={2} alignItems="center" py={6}>
              <ClipboardList sx={{ fontSize: 56, color: 'text.disabled' }} />
              <Typography variant="body1" color="text.secondary">
                No hay planes disponibles en este momento.
              </Typography>
            </Stack>
          )}
        </Stack>
      </>
    );
  }

  // Vista Colombia (COP)
  return (
    <>
      <SEOPageTitle title={t('plan.requests.myTitle', { ns: 'common' })} />

      <Stack spacing={3} sx={{ maxWidth: 700, mx: 'auto' }}>
        {recentLoading && (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={24} color="secondary" />
          </Stack>
        )}

        {!recentLoading && recentPlans.length > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              Tus planes recientes
            </Typography>
            <Grid container spacing={2}>
              {recentPlans.map((item) => {
                const mdCols = { 1: 12, 2: 6, 3: 4 };
                return (
                  <Grid key={item.plan.id} item xs={12} sm={recentPlans.length === 1 ? 12 : 6} md={mdCols[recentPlans.length] || 4}>
                    <RecentPlanCard item={item} onRenew={handleRenew} />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {loading && (
          <Stack alignItems="center" py={6}>
            <CircularProgress color="secondary" />
          </Stack>
        )}

        {!loading && requests.length === 0 && recentPlans.length === 0 && (
          <Stack spacing={2} alignItems="center" py={6}>
            <ClipboardList sx={{ fontSize: 56, color: 'text.disabled' }} />
            <Typography variant="body1" color="text.secondary">
              {t('plan.requests.myEmpty', { ns: 'common' })}
            </Typography>
            <Button
              component={RouterLink}
              to={`${routes.listClientPayments.client.path}?action=select-plan`}
              variant="outlined"
              color="secondary">
              {t('plan.requests.viewAvailable', { ns: 'common' })}
            </Button>
          </Stack>
        )}

        {!loading && requests.map((req) => {
          const cfg = statusConfig[req.status] ?? { label: req.status, color: 'default' };
          const createdDate = new Date(req.created_at).toLocaleDateString('es-CO');

          return (
            <Box key={req.id} sx={planCardSx}>
              <Logo style={watermarkLogoStyles} />
              <Stack spacing={1.5} sx={{ position: 'relative' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {req.plan.name}
                  </Typography>
                  <Chip label={cfg.label} color={cfg.color} size="small" icon={cfg.icon} sx={{ fontWeight: 600 }} />
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  {t('plan.requests.requestedOn', { ns: 'common', date: createdDate })}
                </Typography>

                {req.status === 'PENDING' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t('plan.requests.pendingMessage', { ns: 'common' })}
                  </Alert>
                )}
                {req.status === 'APPROVED' && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    {t('plan.requests.approvedMessage', { ns: 'common' })}
                  </Alert>
                )}
                {req.status === 'REJECTED' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Alert severity="error">
                      {t('plan.requests.rejectedMessage', { ns: 'common' })}
                    </Alert>
                    {req.rejection_reason && (
                      <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'surface.overlay' }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          {t('plan.requests.rejectionReasonLabel', { ns: 'common' })}
                        </Typography>
                        <Typography variant="body2">{req.rejection_reason}</Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </>
  );
}

export default ClientPlanRequestStatus;
